import {defineStore} from "pinia";
import type {FormList} from "@t/fd/layout/FormDefinition.ts";
import {env} from "@fd/Environment.ts";
import {CSSProperties, type Ref, ref} from "vue";
import type {Categories, Category} from "@t/list/Category.ts";
import {useUserStore} from "@/store/UserStore.ts";
import type {ListItem, ListItemBase} from "@t/list/List.ts";


const UG_CAT = new RegExp("(USERS|GROUPS|ALL)(_(USERS|GROUPS|ALL))*");

export interface StoreListItem extends ListItemBase {
	specProps?: Map<string, string>;
}

export type StoreList = StoreListItem[] & {
	loaded: boolean;
};


export interface ListId {
	serverId: string;
	cacheId: string;
	forceLoad?: boolean;
}

/**
 * Splits string by separator and trims each part.
 */
export function breakString(input: string, separator: string): string[] {
	return input
		.split(separator)
		.map(s => s.trim())
		.filter(s => s.length > 0);
}

/**
 * Parses parameter string into key-value map.
 */
export function parseParams(spec: string | null | undefined): Map<string, string> {
	const pars: Map<string, string> = new Map();
	if (!spec || spec.trim().length === 0) {
		return pars;
	}
	const pairs = breakString(spec, ",");

	for (const pair of pairs) {
		const keyVal = breakString(pair, "=");
		if (keyVal.length == 1) {
			// Boolean param
			pars.set(keyVal[0], "true");
		} else if (keyVal.length == 2) {
			pars.set(keyVal[0], keyVal[1]);
		}
	}
	return pars;
}

export function parseStyleParams(spec:string | null | undefined, scope:string = null):CSSProperties {
	let sp = parseParams(spec);
	return propsToStyle(sp, scope);
}

export function propsToStyle(sp:Map<string, string>|null|undefined, scope:string = null):CSSProperties {
	var css:CSSProperties = {};
	if (sp == null) return css;
	if(!scope) {
		if (sp.has("b")) css.fontWeight = "bold";
		if (sp.has("i")) css.fontStyle = "italic";
		if (sp.has("u")) css.textDecoration = "underline";
		if (sp.has("s")) css.textDecoration = "line-through";
		if (sp.has("c")) css.color = sp.get("c");
		if (sp.has("bc")) css.backgroundColor = sp.get("bc");
		if (sp.has("bd")) css.borderColor = sp.get("bd");
	} else {
		if (sp.has(scope+".b")) css.fontWeight = "bold";
		if (sp.has(scope+".i")) css.fontStyle = "italic";
		if (sp.has(scope+".u")) css.textDecoration = "underline";
		if (sp.has(scope+".s")) css.textDecoration = "line-through";
		if (sp.has(scope+".c")) css.color = sp.get("c");
		if (sp.has(scope+".bc")) css["--bg-tinted"] = "color-mix(in srgb, "+sp.get(scope+".bc")+" 20%, var(--bg))";
	}
	return css;
}


export const useListStore = defineStore('list', () => {

	const listCache = new Map<string, Ref<StoreList>>();
	const categoryCache = new Map<string, Ref<Categories>>();
	const us = useUserStore();


	function addListItem(l:Ref<StoreList>, li: ListItemBase) {
		if (li.hasOwnProperty("spec")) {
			l.value.push(<StoreListItem>{ value: li.value, name: li.name, specProps: parseParams((li as ListItem).spec) })
		} else {
			l.value.push(li);
		}
	}

	/**
	 * Install a list to the global list cache
	 * @param id unique ID of the list
	 * @param list	items
	 */
	function installList(id: string, list: FormList):Ref<StoreList> {
		let l: Ref<StoreList>;
		if (listCache.has(id)) {
			l = listCache.get(id);
			l.value.length = 0;		// erase current list items (will be updated)
			l.value.loaded = false;
		} else {
			l = ref<StoreList>(Object.assign([], { loaded: false }));
		}
		for (let li of list) {
			addListItem(l, li);
		}
		l.value.loaded = true;
		listCache.set(id, l);
		return l;
	}

	function installCategory(id: string, cat: Categories):Ref<Categories> {
		let c: Ref<Categories>;
		if (categoryCache.has(id)) {
			c = categoryCache.get(id);
			c.value.length = 0;		// erase current category items (will be updated)
		} else {
			c = ref<Categories>([]);
		}
		for (let ci of cat) c.value.push(ci);
		categoryCache.set(id, c);
		return c;
	}


	function fillUserList(users: Ref<StoreList>, includeInactive: boolean, byName:boolean, ...values: any): void {
		/* { "id": 21, "active": true, "name": "Daniel Mrkvička", "login": "mrkvicka", "access": "STANDARD" } */
		us.allUsers.then((serverList) => {
			for (let u of serverList) {
				if ((u.active && u.allowed) || includeInactive || values.indexOf(u.login) >= 0) {
					users.value.push({
						value: byName ? u.login : u.id.toString(),
						name: u.name,
					});
				}
			}
		}).finally(() => { users.value.loaded = true; });
	}


	function fillUserCategories(cats: Ref<Categories>, includeInactive: boolean, ...values: any): Promise<any> {
		return us.allUsers.then((serverList) => {
			for (let u of serverList) {
				if ((u.active && u.allowed) || includeInactive || values.indexOf(u.login) >= 0) {
					cats.value.push({
						id: u.login,
						title: u.name,
					});
				}
			}
		});

	}

	function fillGroupList(groups: Ref<StoreList>, includeInactive: boolean, byName: boolean, ...values: any): void {
		/* { "id": 13, "active": true, "login": "MGMT", "name": "Management", "type": "STANDARD" } */
		us.allGroups.then((serverList) => {
			for (let g of serverList) {
				if ((g.active && g.allowed) || includeInactive || values.indexOf(g.login) >= 0)
					groups.value.push({
						value: byName ? g.login : g.id.toString(),
						name: g.name,
					});
			}
		}).finally(() => { groups.value.loaded = true; });
	}

	function fillGroupCategories(cats: Ref<Categories>, includeInactive: boolean, ...values: any): Promise<any> {
		/* { "id": 13, "active": true, "login": "MGMT", "name": "Management", "type": "STANDARD" } */
		return us.allGroups.then((serverList) => {
			for (let g of serverList) {
				if ((g.active && g.allowed) || includeInactive || values.indexOf(g.login) >= 0)
					cats.value.push({
						id: g.login,
						title: g.name,
					});
			}
		});
	}

	/**
	 * @param {string} id of the list
	 * @param values optinal list of values that returned list should contain
	 */
	function findList(id: string|ListId, ...values: any):Ref<StoreList> {
		
		let serverId:string, cacheId:string, force:boolean;
		if (typeof id === "string") {
			serverId = cacheId = id;
			force = false;
		} else {
			serverId = id.serverId;
			cacheId = id.cacheId;
			force = !!id.forceLoad;
		}

		let l:Ref<StoreList>;
		if (listCache.has(cacheId)) {
			l = listCache.get(cacheId);
			if (!force) return l;		// Serve from the cache
		} else {
			l = ref<StoreList>(Object.assign([], { loaded: false }));
			listCache.set(cacheId, l);
		}
		// The list must be loaded from the backend
		if (id === "USERS") fillUserList(l, false, true, values);
		else if (id === "GROUPS") fillGroupList(l, false, true, values);
		else if (id === "ALL_USERS") fillUserList(l, true, true, values);
		else if (id === "ALL_GROUPS") fillGroupList(l, true, true, values);
		else if (id === "USER_IDS") fillUserList(l, false, false, values);
		else if (id === "GROUP_IDS") fillGroupList(l, false, false, values);
		else if (id === "ALL_USER_IDS") fillUserList(l, true, false, values);
		else if (id === "ALL_GROUP_IDS") fillGroupList(l, true, false, values);
		else {
			env.loadList(serverId)
				.then((flist) => {
					l.value.length = 0;
					for (let fitem of flist) {
						addListItem(l, fitem);
					}
				})
				.catch((err) => {
					console.error("List " + serverId + " cannot be loaded", err);
					l.value.length = 0;
					l.value.push({value: "", name: "Error loading list " + id})
				})
				.finally(() => {
					l.value.loaded = true;
				});
		}
		return l;
	}

	function findCategory(id: string, ...values: any):Ref<Categories> {

		if (categoryCache.has(id)) return categoryCache.get(id);
		const c = ref<Categories>([]);
		categoryCache.set(id, c);

		if (UG_CAT.test(id)) {
			const parts = id.split("_");
			let includeInactive = false;
			for (let p of parts) {
				if (p === "ALL") includeInactive = true;
				else if (p === "USERS") fillUserCategories(c, includeInactive, values);
				else if (p === "GROUPS") fillGroupCategories(c, includeInactive, values);
			}
		} else {
			env.loadCategories(id)
				.then((cats) => {
					c.value.length = 0;
					for (let citem of cats) c.value.push(citem);
				})
				.catch((err) => {
					console.error("Category " + id + " cannot be loaded", err);
					c.value.length = 0;
					c.value.push({id: "", title: "Error loading cats " + id});
				});
		}
		return c;
	}

	return {
		installList, findList,
		installCategory, findCategory
	}
});
